/**
 * Bell + dropdown that shows tenants approaching/over plan limits.
 *
 * Drives off `GET /api/super-admin/alerts`. Polls every 60s while mounted.
 * Click → list with detailed alerts per tenant + CTAs to edit limits / users.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, ArrowUpRight, ShieldAlert, X as XIcon } from "lucide-react";
import { api } from "../lib/api";
import { tenantColor, initials } from "../lib/tenantVisuals";
import { logError } from "../lib/logger";

const RESOURCE_LABEL = {
  collaborators: "Colaboradores",
  clients: "Clientes",
  teams: "Equipes",
};

const POLL_MS = 60_000;

export function PlatformAlertsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const wrapRef = useRef(null);

  async function load() {
    try {
      const { data } = await api.get("/super-admin/alerts");
      setData(data || []);
    } catch (e) {
      // não polui o painel com erros — fallback silencioso
      logError("Failed to load alerts", e);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // close on click outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const dangerCount = data.filter((d) => d.max_level === "danger").length;
  const total = data.length;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="platform-alerts-bell"
        title={`${total} empresa(s) próximas ou no limite do plano`}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-lg border transition ${
          dangerCount > 0
            ? "bg-red-500/15 border-red-500/40 text-red-200 hover:bg-red-500/25"
            : total > 0
              ? "bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
              : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
        }`}
      >
        <Bell size={18} />
        {total > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center border ${
              dangerCount > 0
                ? "bg-red-500 text-white border-red-300"
                : "bg-amber-400 text-amber-950 border-amber-200"
            }`}
            data-testid="platform-alerts-badge"
          >
            {total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[420px] max-w-[92vw] bg-slate-950/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden"
          data-testid="platform-alerts-panel"
        >
          <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-violet-900/40 to-fuchsia-900/30">
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-amber-300" />
              <h3 className="text-sm font-bold text-white">
                Alertas de limite de plano
              </h3>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-white/10 text-slate-400"
              aria-label="Fechar"
            >
              <XIcon size={14} />
            </button>
          </header>

          <div className="max-h-[60vh] overflow-y-auto">
            {total === 0 ? (
              <div className="px-4 py-10 text-center text-slate-400 text-sm">
                <div className="inline-flex w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-300 items-center justify-center mb-2">
                  ✓
                </div>
                <div>Todas as empresas dentro dos limites do plano.</div>
                <div className="text-xs text-slate-500 mt-1">
                  Verifica novamente a cada 60s.
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.map((row) => (
                  <AlertItem
                    key={row.tenant_id}
                    row={row}
                    onNavigate={(path) => {
                      setOpen(false);
                      nav(path);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>

          {total > 0 && (
            <footer className="px-4 py-2 border-t border-white/10 bg-slate-900/60 text-[11px] text-slate-400 flex items-center justify-between">
              <span>
                {dangerCount > 0
                  ? `${dangerCount} no limite · ${total - dangerCount} próximo`
                  : `${total} próximo do limite`}
              </span>
              <button
                onClick={() => {
                  setOpen(false);
                  nav("/super-admin/tenants");
                }}
                className="text-violet-300 hover:text-violet-200 font-semibold"
              >
                Ver todas →
              </button>
            </footer>
          )}
        </div>
      )}
    </div>
  );
}

function AlertItem({ row, onNavigate }) {
  const c = tenantColor(row.tenant_id);
  const isDanger = row.max_level === "danger";
  return (
    <li
      className="px-4 py-3 hover:bg-white/5 transition"
      data-testid={`alert-item-${row.tenant_id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}
        >
          {initials(row.tenant_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-white text-sm truncate">
              {row.tenant_name}
            </h4>
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${
                isDanger
                  ? "bg-red-500/20 text-red-200 border-red-500/40"
                  : "bg-amber-500/20 text-amber-200 border-amber-500/40"
              }`}
            >
              <AlertTriangle size={9} />
              {isDanger ? "no limite" : "perto"}
            </span>
            {row.plan_id && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200 border border-white/10">
                {row.plan_id}
              </span>
            )}
          </div>

          <div className="mt-1.5 space-y-1">
            {row.alerts.map((a) => (
              <div
                key={a.resource}
                className="flex items-center gap-2 text-xs text-slate-300"
              >
                <span className="w-24 text-slate-400">
                  {RESOURCE_LABEL[a.resource] || a.resource}
                </span>
                <span
                  className={`font-bold ${
                    a.level === "danger" ? "text-red-300" : "text-amber-300"
                  }`}
                >
                  {a.current}/{a.limit}
                </span>
                <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, a.percent)}%`,
                      background: a.level === "danger" ? "#ef4444" : "#f59e0b",
                    }}
                  />
                </div>
                <span className="text-[10px] text-slate-500 w-10 text-right">
                  {a.percent}%
                </span>
              </div>
            ))}
          </div>

          <div className="mt-2 flex gap-2">
            <button
              onClick={() => onNavigate(`/super-admin/tenants`)}
              data-testid={`alert-item-action-limits-${row.tenant_id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
            >
              <ArrowUpRight size={11} /> Limites
            </button>
            {row.alerts.some((a) => a.resource === "collaborators") && (
              <button
                onClick={() => onNavigate(`/super-admin/collaborators`)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200"
              >
                Ver colaboradores
              </button>
            )}
            {row.alerts.some((a) => a.resource === "clients") && (
              <button
                onClick={() => onNavigate(`/super-admin/clients`)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200"
              >
                Ver clientes
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
